import { CircleNotchIcon, EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { BrandLogo } from "@/components/brand-logo";
import {
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  PasswordField,
  passwordProblem,
} from "@/components/password-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiErrorDetail, apiErrorStatus, useResetPassword } from "@/lib/api";
import { PROJECT_NAME } from "@/lib/brand-assets";
import { logger } from "@/lib/logger";
import { PASSWORD_RESET_NOTICE } from "@/lib/login-notice";
import { retryAfterLabel } from "@/lib/retry-after";

// Constant-length JSX expression keeps prettier output stable for any rendered name.

const TOKEN_PARAM = "token_hash";
const TYPE_PARAM = "type";
const RECOVERY_TYPE = "recovery";

const INCOMPLETE_LINK = "This reset link is incomplete. Request a new one below.";
const USED_LINK = "This reset link has expired or was already used.";
const REJECTED_PASSWORD =
  "That password was rejected. Request a new link and choose a stronger password.";

/**
 * Where the recovery email's link lands: `/reset-password?token_hash=…&type=recovery`.
 * The token is read once and posted with the new password; it never sets a session,
 * so the user signs in afterwards.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const resetMutation = useResetPassword();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once on mount: the effect below strips the token from the address bar.
  const [tokenHash] = useState(() =>
    searchParams.get(TYPE_PARAM) === RECOVERY_TYPE ? searchParams.get(TOKEN_PARAM) || null : null,
  );
  // Set once the link can't be used any more; the form then gives way to a way
  // to request a new one.
  const [linkProblem, setLinkProblem] = useState(tokenHash === null ? INCOMPLETE_LINK : "");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  // Keep the one-time token out of the history, bookmarks and screenshots.
  useEffect(() => {
    if (searchParams.has(TOKEN_PARAM) || searchParams.has(TYPE_PARAM)) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const problem = passwordProblem(password, confirmation);
    if (problem) {
      setError(problem);
      return;
    }
    if (tokenHash === null) {
      return;
    }
    logger.debug("[auth] setting a new password from a reset link");
    resetMutation.mutate(
      { tokenHash, newPassword: password },
      {
        onSuccess: () => {
          logger.debug("[auth] password reset → /login");
          navigate("/login", { replace: true, state: PASSWORD_RESET_NOTICE });
        },
        onError: (err) => {
          const status = apiErrorStatus(err);
          const detail = apiErrorDetail(err);
          logger.debug("[auth] password reset failed:", status, detail);
          if (status === 400 && detail === "reset_link_invalid") {
            setLinkProblem(USED_LINK);
          } else if (status === 400 && detail === "password_rejected") {
            setLinkProblem(REJECTED_PASSWORD);
          } else {
            setError(resetErrorMessage(err));
          }
        },
      },
    );
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4 py-10">
      <Card variant="box" className="w-full max-w-md">
        <CardHeader className="space-y-6 text-left">
          <p className="text-muted t-eyebrow">Board of Innovation</p>
          <BrandLogo name={PROJECT_NAME} className="h-7 text-2xl" />
          <div className="space-y-2">
            <CardTitle className="t-title text-foreground">Choose a new password</CardTitle>
            <p className="text-muted text-sm leading-6">
              Use {MIN_PASSWORD_LENGTH} to {MAX_PASSWORD_LENGTH} characters. You&apos;ll sign in
              with it next.
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {linkProblem ? (
            <div className="space-y-4">
              <p role="alert" className="text-destructive text-sm">
                {linkProblem}
              </p>
              <Button
                asChild
                className="bg-accent text-accent-foreground hover:bg-accent-hover h-12 w-full rounded-sm font-semibold"
              >
                <Link to="/forgot-password">Request a new link</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <PasswordField
                id="newPassword"
                label="New password"
                value={password}
                onChange={setPassword}
                visible={showPassword}
              />
              <PasswordField
                id="confirmPassword"
                label="Confirm new password"
                value={confirmation}
                onChange={setConfirmation}
                visible={showPassword}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-muted hover:text-foreground flex items-center gap-2 text-xs transition-colors"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
                {showPassword ? "Hide passwords" : "Show passwords"}
              </button>
              {error && (
                <p role="alert" className="text-destructive text-sm">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={resetMutation.isPending}
                className="bg-accent text-accent-foreground hover:bg-accent-hover h-12 w-full rounded-sm font-semibold"
              >
                {resetMutation.isPending ? (
                  <>
                    <CircleNotchIcon className="h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Set new password"
                )}
              </Button>
            </form>
          )}
          <p className="text-center text-sm">
            <Link to="/login" className="text-accent-text hover:underline">
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function resetErrorMessage(err: unknown): string {
  const status = apiErrorStatus(err);
  if (status === 403) {
    return "This account can't reset its password here.";
  }
  if (status === 429) {
    const wait = retryAfterLabel(err);
    return wait
      ? `Too many attempts. Try again in ${wait}.`
      : "Too many attempts. Try again later.";
  }
  return "Something went wrong. Please try again.";
}
